import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, LinearProgress, Typography, Chip, Stack, useMediaQuery, useTheme } from "@mui/material";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { HPC_COLORS } from "./hpcTheme";
import { HPC_BRAND } from "./hpcBrand";
import { STEPS } from "./hpcFormConfig";

/** Extra space after the last field on every step so nothing sits under the button bar. */
const STEP_END_PADDING = 32;

/**
 * Mobile: Back/Continue live at the end of the scroll (no overlay on fields).
 * Desktop: footer pinned below a dedicated scroll region.
 */
const HpcShell = ({ activeStep, children, footer, steps = STEPS, wizardTitle = "Membership registration" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(80);
  const progress = ((activeStep + 1) / steps.length) * 100;

  useLayoutEffect(() => {
    const node = footerRef.current;
    if (!node) return undefined;

    const update = () => setFooterHeight(node.offsetHeight || 80);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [footer, isMobile]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [activeStep]);

  const footerStyles = {
    width: "100%",
    background: "rgba(10, 22, 40, 0.98)",
    backdropFilter: "blur(14px)",
    borderTop: `1px solid rgba(245, 166, 35, 0.35)`,
    px: 2,
    pt: 1.5,
    pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
    display: "flex",
    gap: 1,
    boxShadow: isMobile ? "none" : "0 -4px 24px rgba(0,0,0,0.35)",
  };

  return (
    <Box
      className="hpc-register-layout"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: `
          radial-gradient(ellipse 80% 50% at 50% -10%, rgba(43, 124, 211, 0.25) 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 100% 50%, rgba(245, 166, 35, 0.12) 0%, transparent 50%),
          linear-gradient(180deg, ${HPC_COLORS.navy} 0%, ${HPC_COLORS.navyMid} 45%, ${HPC_COLORS.navy} 100%)
        `,
      }}
    >
      <Box
        ref={scrollRef}
        className="hpc-register-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
          ...(isMobile
            ? {}
            : { height: `calc(100% - ${footerHeight}px)` }),
          "& input, & textarea, & select": {
            scrollMarginBottom: "120px",
          },
          "& .MuiFormControlLabel-root": {
            scrollMarginBottom: "120px",
          },
        }}
      >
        <Box
          sx={{
            maxWidth: 720,
            mx: "auto",
            px: 2,
            pt: 2.5,
            pb: isMobile ? 2 : 3,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2.5, pt: 1 }}>
            <Box
              component="img"
              src={HPC_BRAND.logoSrc}
              alt={HPC_BRAND.name}
              sx={{
                width: "min(200px, 55vw)",
                height: "auto",
                mx: "auto",
                display: "block",
                filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.45))",
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                mt: 1.5,
                color: HPC_COLORS.white,
                fontWeight: 700,
                letterSpacing: "0.02em",
                lineHeight: 1.35,
              }}
            >
              {HPC_BRAND.name}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                mt: 0.75,
                color: HPC_COLORS.silver,
                letterSpacing: "0.04em",
                textTransform: "none",
                fontSize: "0.85rem",
              }}
            >
              {HPC_BRAND.tagline}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mt: 2,
              }}
            >
              <FitnessCenterIcon sx={{ color: HPC_COLORS.hpuOrange, fontSize: 22 }} />
              <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 0 }}>
                {wizardTitle}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: HPC_COLORS.hpuOrange, fontWeight: 700 }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography variant="caption" sx={{ color: HPC_COLORS.silver }}>
                {steps[activeStep].label}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mb: 2.5,
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(245,166,35,0.4)",
                borderRadius: 2,
              },
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isDone = index < activeStep;
              return (
                <Chip
                  key={step.key}
                  size="small"
                  label={`${index + 1}. ${step.label}`}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    border: "1px solid",
                    borderColor: isActive
                      ? HPC_COLORS.hpuOrange
                      : isDone
                        ? "rgba(43, 124, 211, 0.6)"
                        : "rgba(255,255,255,0.12)",
                    bgcolor: isActive
                      ? "rgba(245, 166, 35, 0.2)"
                      : isDone
                        ? "rgba(43, 124, 211, 0.15)"
                        : "rgba(0,0,0,0.2)",
                    color: isActive ? HPC_COLORS.hpuOrange : isDone ? HPC_COLORS.tebaBlue : HPC_COLORS.silver,
                  }}
                />
              );
            })}
          </Stack>

          <Box
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 2.5 },
              background: HPC_COLORS.navyCard,
              border: `1px solid rgba(245, 166, 35, 0.25)`,
              boxShadow: `0 12px 40px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.06)`,
              backdropFilter: "blur(8px)",
            }}
          >
            <Box className="hpc-step-content" sx={{ pb: `${STEP_END_PADDING}px` }}>
              {children}
            </Box>
          </Box>

          {isMobile && footer && (
            <Box
              ref={footerRef}
              className="hpc-register-footer"
              sx={{ ...footerStyles, mt: 2, borderRadius: 2 }}
            >
              {footer}
            </Box>
          )}
        </Box>
      </Box>

      {!isMobile && footer && (
        <Box
          ref={footerRef}
          className="hpc-register-footer"
          sx={{
            flexShrink: 0,
            maxWidth: 720,
            width: "100%",
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Box sx={footerStyles}>{footer}</Box>
        </Box>
      )}
    </Box>
  );
};

export default HpcShell;
